import { emojiData } from '@whatsapp/data/emojis'

const STORAGE_KEY = 'emojiUsage'

interface EmojiUsage {
    emoji: string
    count: number
    lastUsed: number
}

/**
 * Get emoji usage data from localStorage
 */
function getEmojiUsage(): EmojiUsage[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
            return JSON.parse(stored)
        }
    } catch (error) {
        console.error('Error loading emoji usage:', error)
    }
    return []
}

/**
 * Save emoji usage data to localStorage
 */
function saveEmojiUsage(usage: EmojiUsage[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(usage))
    } catch (error) {
        console.error('Error saving emoji usage:', error)
    }
}

/**
 * Record that an emoji was used
 */
export function recordEmojiUsage(emoji: string): void {
    const usage = getEmojiUsage()
    const existing = usage.find(u => u.emoji === emoji)
    
    if (existing) {
        existing.count++
        existing.lastUsed = Date.now()
    } else {
        usage.push({
            emoji,
            count: 1,
            lastUsed: Date.now()
        })
    }
    
    // Keep only top 20 most used
    usage.sort((a, b) => {
        // Sort by count first, then by lastUsed if counts are equal
        if (b.count !== a.count) {
            return b.count - a.count
        }
        return b.lastUsed - a.lastUsed
    })
    
    // Keep top 20
    const top20 = usage.slice(0, 20)
    saveEmojiUsage(top20)
}

/**
 * Get top 20 most used emojis
 * If no usage data exists, returns top 20 from emojiData
 */
export function getTopEmojis(): string[] {
    const usage = getEmojiUsage()
    
    if (usage.length === 0) {
        // Return first 20 emojis from emojiData if no usage data
        return emojiData.slice(0, 20).map(e => e.emoji)
    }
    
    // Return top 20 from usage data, sorted by count then lastUsed
    return usage
        .sort((a, b) => {
            if (b.count !== a.count) {
                return b.count - a.count
            }
            return b.lastUsed - a.lastUsed
        })
        .slice(0, 20)
        .map(u => u.emoji)
}

